import { extractUnitSuffix, initialsOf, type UnitSuffixSource } from './initials';
import { formatQuoteDateCode } from './quote-date';
import { activeQuoteNumberPrefix } from '../services/business-quote-prefix';
import { QUOTE_PREFIX_RE } from './quote-number-prefix';
import { isDemoBusiness } from '../services/demo-business';

// THE TITLE PREFIX BELONGS TO THE BUSINESS. A generated title reads
// "QUOTE <prefix>/<street, type and suburb initials> (<MMYY>)". The prefix is
// the one the business's Quote # prints (services/business-quote-prefix.ts):
// its stored quote_number_prefix, or the initials of its business name when it
// set none (decision D5 = O1, utils/quote-number-prefix.ts). RJ Bird stores
// RBB, and its name gives RBB too, so its titles are unchanged.
// No business ever borrows RJ Bird's "RBB". Until the business's prefix is
// known, a new title carries the stand-in "XXX", which no title check
// accepts, so a cold cache stops the run with PROJECT_TITLE_UNRESOLVED instead
// of naming a quote after another business.
// Once the prefix is known, only titles with that prefix are generated
// titles: an RBB title saved for another business is regenerated unless the
// operator wrote it (titleEditedByUser). While the prefix is unknown, a saved
// title with any prefix is kept as it is, so nothing is regenerated from a
// guess; a demo business keeps its stricter rule and accepts none until then.
const UNRESOLVED_TITLE_PREFIX = 'XXX';
/** The same rule as tenant_profiles.quote_number_prefix (migration 091). */
const TITLE_PREFIX_RE = /^[A-Za-z0-9]{1,8}$/;
/** Any generated title while the business is unknown: a prefix with a letter, never the XXX stand-in. */
const ANY_GENERATED_TITLE_RE = /^QUOTE\s+(?!XXX\/)(?=[A-Z0-9]*[A-Z])[A-Z0-9]{1,8}\/(?!XXX\b)[A-Z0-9]+(?:\s|\()/i;

function normaliseTitlePrefix(value: unknown): string {
    const prefix = typeof value === 'string' ? value.trim() : '';
    return TITLE_PREFIX_RE.test(prefix) ? prefix.toUpperCase() : '';
}

function knownTitlePrefix(): string {
    return normaliseTitlePrefix(activeQuoteNumberPrefix());
}

/**
 * The signed-in business's title prefix, read synchronously like
 * formatQmQuoteNoDisplay reads it for the Quote #. 'XXX' while it is not known.
 */
export function activeQuoteTitlePrefix(): string {
    return knownTitlePrefix() || UNRESOLVED_TITLE_PREFIX;
}

/** What an untitled quote shows: "QUOTE HLB/XXX (MMYY)". */
export function placeholderQuoteTitle(prefix: string = activeQuoteTitlePrefix()): string {
    return `QUOTE ${normaliseTitlePrefix(prefix) || UNRESOLVED_TITLE_PREFIX}/XXX (MMYY)`;
}

/**
 * The prefixes a generated title may carry: this business's own. null while
 * it is not known (any prefix, see ANY_GENERATED_TITLE_RE); a demo business
 * accepts none until then.
 */
function generatedTitlePrefixes(): string[] | null {
    const own = knownTitlePrefix();
    if (own) return [own];
    return isDemoBusiness() ? [] : null;
}

const STREET_TYPES = [
    'Esplanade', 'Boulevard', 'Crescent', 'Terrace', 'Highway', 'Circuit',
    'Parade', 'Avenue', 'Street', 'Square', 'Close', 'Grove', 'Place',
    'Court', 'Drive', 'Plaza', 'Track', 'Walk', 'Mews', 'Rise', 'Road',
    'Lane', 'Blvd', 'Cres', 'Cct', 'Pde', 'Ave', 'Tce', 'Hwy', 'Esp',
    'Grv', 'Plz', 'Trk', 'Wlk', 'Way', 'Sq', 'St', 'Rd', 'Dr', 'Ln',
    'Pl', 'Ct',
] as const;

const STATE_NAMES = [
    'NSW', 'VIC', 'QLD', 'SA', 'WA', 'TAS', 'NT', 'ACT',
    'New South Wales', 'Victoria', 'Queensland', 'South Australia',
    'Western Australia', 'Tasmania', 'Northern Territory',
    'Australian Capital Territory',
] as const;

export type QuoteTitleFormData = UnitSuffixSource & {
    address?: string | null;
    quoteDate?: unknown;
};

export type GenerateRbbQuoteTitleOptions = {
    formData?: QuoteTitleFormData | null;
    address?: unknown;
    quoteDate?: unknown;
    fallbackDate?: Date;
    /** Overrides the signed-in business's prefix. */
    prefix?: string;
};

/**
 * The stand-in a quote with no address shows: "QUOTE <prefix>/XXX (MMYY)", for
 * any business. A blank title counts as one too. It names no job.
 */
export function isPlaceholderQuoteTitle(value: unknown): boolean {
    const title = String(value ?? '').trim();
    return !title || /^QUOTE\s+[A-Za-z0-9]{1,8}\s*\/\s*XXX\b/i.test(title);
}

/**
 * What a project is saved as when nothing names it ("Untitled Project", the
 * stand-in the save writes, or the "Untitled project" the lists show). It names
 * no job, so it is never kept as a title or adopted as one.
 */
export function isUntitledQuoteTitle(value: unknown): boolean {
    return /^untitled(?:\s+project)?$/i.test(String(value ?? '').trim());
}

const CONTACT_TITLE_FIELDS = ['clientName', 'clientCompany', 'attentionTo', 'contactName'] as const;

function normaliseTitleText(value: unknown): string {
    return String(value ?? '').trim().replace(/\s+/g, ' ').toLowerCase();
}

/**
 * A title that is only a name from the request: the person the quote is
 * addressed to (the strata manager), or their firm. It names no job. Until
 * 2026-10-20 a save with a blank address wrote `clientName` as the title, and
 * from 6.0.92 the tab adopted that saved title and kept it once the address
 * arrived, so quotes printed the strata manager's name where the title goes.
 */
export function isContactNameQuoteTitle(value: unknown, formData: unknown): boolean {
    const title = normaliseTitleText(value);
    if (!title || !formData || typeof formData !== 'object') return false;
    const fields = formData as Record<string, unknown>;
    return CONTACT_TITLE_FIELDS.some(field => normaliseTitleText(fields[field]) === title);
}

/**
 * A title that is only the job description, as a save with no address writes it
 * (the description, cut to 77 characters and "..." when longer). It stands in
 * until the address arrives, then the generated code replaces it: the title
 * starts "QUOTE RBB/" (Peter, 2026-10-31). Before, it was kept as a real title,
 * so a job whose address arrived after its first save printed the description.
 */
export function isJobDescriptionQuoteTitle(value: unknown, formData: unknown): boolean {
    const title = normaliseTitleText(value);
    if (!title || !formData || typeof formData !== 'object') return false;
    const description = normaliseTitleText((formData as Record<string, unknown>).jobDescription);
    if (!description) return false;
    if (title === description) return true;
    const cut = title.endsWith('...') ? title.slice(0, -3).trimEnd() : '';
    return cut.length > 0 && description.startsWith(cut);
}

/** A generated address code ("QUOTE RBB/GSC (0526)", any prefix, never the XXX stand-in). */
function isGeneratedCodeTitle(value: unknown): boolean {
    return /^QUOTE\s+[A-Za-z0-9]{1,8}\/(?!XXX\b)[A-Za-z0-9]+(?:\s|\()/i.test(String(value ?? '').trim());
}

/**
 * The title this tab should show after a project save, given the title the
 * save SENT and the title the server row now holds, or null to leave the tab
 * alone. The server row can hold a descriptive title (from the job description)
 * where the tab only has the placeholder a blank address generates; without
 * this the header, the quote and the PDF keep saying "XXX" until the project is
 * reopened. Only a title that names no job (the placeholder, "Untitled", blank)
 * is ever replaced: a title the operator wrote (titleEditedByUser), or any real
 * title already showing, is never touched, and the server's own placeholder or
 * "Untitled" is never adopted.
 */
export function adoptCanonicalQuoteTitle({
    sentTitle,
    titleEditedByUser = false,
    canonicalTitle,
    formData,
}: {
    sentTitle: unknown;
    titleEditedByUser?: boolean;
    canonicalTitle: unknown;
    /** The project's request fields, so a contact's name is never adopted as the title. */
    formData?: unknown;
}): string | null {
    if (titleEditedByUser) return null;
    if (!isPlaceholderQuoteTitle(sentTitle) && !isUntitledQuoteTitle(sentTitle)) return null;
    const canonical = String(canonicalTitle ?? '').trim();
    if (isPlaceholderQuoteTitle(canonical) || isUntitledQuoteTitle(canonical)) return null;
    if (isContactNameQuoteTitle(canonical, formData)) return null;
    return canonical;
}

/**
 * The next automatic title when the address, unit or job description changes
 * and the operator has not written a title. A real descriptive title (one
 * adopted from the saved row, or loaded) is kept whatever title would be
 * generated: adding or changing the address, or editing the job description,
 * must not turn it into the "XXX" a blank address makes, nor into an address
 * code. Only a title that names no job follows the generation: a generated
 * address code ("QUOTE RBB/GSC (0526)"), the placeholder, a blank and
 * "Untitled". A title that is only the job description follows it too, once
 * the address makes a real code.
 */
export function nextAutoQuoteTitle({
    previousTitle,
    generatedTitle,
    formData,
}: {
    previousTitle: unknown;
    generatedTitle: string;
    /** The project's request fields: a title that is only a contact's name follows the generation. */
    formData?: unknown;
}): string {
    if (!isPlaceholderQuoteTitle(previousTitle)
        && !isUntitledQuoteTitle(previousTitle)
        && !isGeneratedCodeTitle(previousTitle)
        && !isContactNameQuoteTitle(previousTitle, formData)
        && !(isJobDescriptionQuoteTitle(previousTitle, formData) && !isPlaceholderQuoteTitle(generatedTitle))) {
        return String(previousTitle).trim();
    }
    return generatedTitle;
}

/**
 * A title for a quote that has neither a title nor an address, from the QM
 * quote number it was actually assigned. The caller passes the number as the
 * app shows it (formatQuoteNumberLabel / formatQmQuoteNoDisplay, which carry
 * the business's own prefix): "QUOTE RBB261039". The raw stored digits are not
 * a title, so the prefix is never invented or guessed here. Empty when no
 * number is known.
 */
export function titleFromAssignedQmQuoteNo(formattedQmQuoteNo: unknown): string {
    const number = typeof formattedQmQuoteNo === 'string' ? formattedQmQuoteNo.trim().slice(0, 64) : '';
    return number ? `QUOTE ${number}` : '';
}

export function cleanCanonicalQuoteTitle(raw: unknown): string {
    const value = String(raw || '').trim();
    if (!value) return '';
    if (/^quote$/i.test(value)) return '';
    // The placeholder of any business ("QUOTE HLB/XXX (MMYY)") is not a title.
    if (/^quote\s+[a-z0-9]{1,8}\s*\/\s*xxx\s*\([^)]*\)$/i.test(value)) return '';
    return value;
}

/**
 * cleanCanonicalQuoteTitle for the title a quote SHOWS: additionally "Untitled"
 * (what a project with no title is saved as) is not a title, so the next
 * fallback applies. Kept separate so quote generation's own title rule is
 * unchanged.
 */
export function cleanDisplayQuoteTitle(raw: unknown): string {
    const value = cleanCanonicalQuoteTitle(raw);
    return isUntitledQuoteTitle(value) ? '' : value;
}

function removeStreetNumber(text: string): string {
    return text
        .replace(/^(Unit\s*\d+\s*[\/\\]?\s*)?/i, '')
        .replace(/^\d+[\-\/]?\d*\s*/, '')
        .trim();
}

function cleanSuburb(text: string): string {
    return text
        .replace(/\s+(NSW|VIC|QLD|SA|WA|TAS|NT|ACT)\s*\d{0,4}\s*$/i, '')
        .replace(/\s+\d{4}\s*$/, '')
        .trim();
}

function matchingStreetType(word: string): string {
    return STREET_TYPES.find(type => type.toLowerCase() === word.toLowerCase()) || '';
}

function splitAddress(address: string): { streetName: string; streetType: string; suburb: string } {
    let streetName = '';
    let streetType = '';
    let suburb = '';
    const commaParts = address.split(',').map(part => part.trim()).filter(Boolean);

    if (commaParts.length >= 2) {
        const unitPrefixPattern = /^(unit|apt|apartment|suite|lot|level|shop|flat|villa)\s+\d+\s*[a-z]?\s*$/i;
        const firstIsUnitPrefix = commaParts.length >= 3 && unitPrefixPattern.test(commaParts[0] || '');
        const streetPart = commaParts[firstIsUnitPrefix ? 1 : 0] || '';
        const suburbSearchStart = firstIsUnitPrefix ? 2 : 1;

        for (let index = suburbSearchStart; index < commaParts.length; index += 1) {
            const part = commaParts[index] || '';
            if (/^\d{4}$/.test(part)) continue;
            if (STATE_NAMES.some(state => state.toLowerCase() === part.toLowerCase())) continue;
            if (/^(NSW|VIC|QLD|SA|WA|TAS|NT|ACT)\s+\d{0,4}/i.test(part)) continue;
            suburb = cleanSuburb(part);
            break;
        }

        const streetWords = removeStreetNumber(streetPart).split(/\s+/).filter(Boolean);
        for (let index = streetWords.length - 1; index >= 0; index -= 1) {
            const candidate = streetWords[index] || '';
            const match = matchingStreetType(candidate);
            if (!match) continue;
            streetType = candidate;
            const withoutNumber = removeStreetNumber(streetPart);
            streetName = withoutNumber.replace(new RegExp(`\\s*${candidate}\\s*$`, 'i'), '').trim();
            break;
        }
        if (!streetType) {
            streetType = streetWords.at(-1) || '';
            streetName = streetWords.slice(0, -1).join(' ') || streetWords[0] || '';
        }
    } else {
        const words = address.split(/\s+/).filter(Boolean);
        let typeIndex = -1;
        for (let index = words.length - 1; index >= 1; index -= 1) {
            if (!matchingStreetType(words[index] || '')) continue;
            typeIndex = index;
            streetType = words[index] || '';
            break;
        }
        if (typeIndex > 0) {
            streetName = words.slice(1, typeIndex).join(' ');
            const suburbWords: string[] = [];
            for (let index = typeIndex + 1; index < words.length; index += 1) {
                const word = words[index] || '';
                if (/^(NSW|VIC|QLD|SA|WA|TAS|NT|ACT)$/i.test(word) || /^\d{4}$/.test(word)) break;
                suburbWords.push(word);
            }
            suburb = suburbWords.join(' ');
        } else if (words.length >= 4) {
            streetName = words[1] || '';
            streetType = words[2] || '';
            suburb = words[3] || '';
        } else if (words.length >= 2) {
            streetName = words[1] || words[0] || '';
        }
    }

    return { streetName, streetType, suburb };
}

export function isResolvedRbbQuoteTitle(value: unknown): boolean {
    const title = String(value || '').trim();
    const prefixes = generatedTitlePrefixes();
    if (prefixes === null) return ANY_GENERATED_TITLE_RE.test(title);
    // Each prefix is 1-8 letters or digits (normaliseTitlePrefix), so it is
    // safe to place in the pattern as it is.
    return prefixes.some(prefix => (
        new RegExp(`^QUOTE\\s+${prefix}\\/(?!XXX\\b)[A-Z0-9]+(?:\\s|\\()`, 'i').test(title)
    ));
}

export function isUsableQuoteTitle(
    value: unknown,
    { titleEditedByUser = false }: { titleEditedByUser?: boolean } = {},
): boolean {
    const title = String(value || '').trim();
    if (!title || /^QUOTE\s+[A-Z0-9]{1,8}\/XXX\b/i.test(title)) return false;
    return titleEditedByUser || isResolvedRbbQuoteTitle(title);
}

/** "QUOTE <prefix>/<initials>", with the prefix captured. */
const SAVED_TITLE_RE = /^QUOTE\s+([A-Z0-9]+)\/(?!XXX\b)[A-Z0-9]+(?:\s|\()/i;

/**
 * Whether a title already SAVED on a quote is a real title: one the operator
 * wrote, or a generated title with ANY valid business prefix (QUOTE_PREFIX_RE),
 * not only the current one. A draft titled before the business changed its
 * prefix in Settings, or titled RBB by the old leak, is still a saved quote,
 * so checking that it persisted must not stop the run. The placeholder
 * ("QUOTE HTB/XXX") is never a title. NEW titles are judged by
 * isUsableQuoteTitle, which accepts only this business's own prefix.
 */
export function isSavedQuoteTitle(
    value: unknown,
    { titleEditedByUser = false }: { titleEditedByUser?: boolean } = {},
): boolean {
    const title = String(value || '').trim();
    if (!title || /^QUOTE\s+[A-Z0-9]{1,8}\/XXX\b/i.test(title)) return false;
    if (titleEditedByUser) return true;
    const prefix = title.match(SAVED_TITLE_RE)?.[1] || '';
    return QUOTE_PREFIX_RE.test(prefix) && prefix.toUpperCase() !== UNRESOLVED_TITLE_PREFIX;
}

/** A source-folder label is temporary intake metadata, not a builder's title. */
export function isImportedFolderTitle(title: unknown, project: Record<string, any> = {}): boolean {
    const value = String(title || '').trim();
    if (!value || project.titleEditedByUser === true || project.title_edited_by_user === true
        || isResolvedRbbQuoteTitle(value)) return false;
    if (project.titleSource === 'source-display' && value === String(project.title || '').trim()) return true;
    const folder = project.projectFolder || project.project_folder || {};
    // A project promoted from Intake is titled with the intake folder name, or the stand-in promotion wrote
    // (intakeProjectTitle) when the job had no folder. Both are intake labels too, compared whole: the stand-in holds
    // the street, which can carry a "/" ("QR99201 — 12/14 Smith Street").
    const intake = project.formData || project.form_data || {};
    if ([intake.intakeFolderName, intake.intakeProjectTitle].some(label => String(label || '').trim() === value)) return true;
    const sources = [project.sourceDisplayName, project.folderName, folder.folderName, folder.subfolderName,
        project.syncRelativePath, project.sync_relative_path, project.relativePath, folder.relativePath];
    return sources.some(source => String(source || '').replace(/\\/g, '/').replace(/\/+$/, '').split('/').pop()?.trim() === value);
}

export function generateRbbQuoteTitle({
    formData = {},
    address,
    quoteDate,
    fallbackDate = new Date(),
    prefix,
}: GenerateRbbQuoteTitleOptions = {}): string {
    const source = formData || {};
    const titlePrefix = normaliseTitlePrefix(prefix) || activeQuoteTitlePrefix();
    const resolvedAddress = String(address ?? source.address ?? '').trim();
    const dateCode = formatQuoteDateCode(quoteDate ?? source.quoteDate, fallbackDate);
    if (!resolvedAddress) return `QUOTE ${titlePrefix}/XXX (${dateCode})`;

    const { streetName, streetType, suburb } = splitAddress(resolvedAddress);
    const streetInitials = initialsOf(streetName);
    const typeInitial = (streetType.charAt(0) || 'X').toUpperCase();
    const suburbInitials = initialsOf(suburb);
    return `QUOTE ${titlePrefix}/${streetInitials}${typeInitial}${suburbInitials} (${dateCode})${extractUnitSuffix({
        ...source,
        address: resolvedAddress,
    })}`;
}

export function splitTitlePrefix(title: string): { prefix: string; rest: string } {
    const m = String(title || '').match(/^(QUOTE\s+)(.+)$/i);
    return m ? { prefix: m[1] as string, rest: m[2] as string } : { prefix: '', rest: String(title || '') };
}
